import { useMutation, useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { Form, Formik } from 'formik'
import { useState } from 'react'
import { Portal } from 'react-portal'
import { useParams } from 'react-router-dom'

import type { FormikHelpers } from 'formik'
import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IPageContainerCreateUpdate, IPageContainerDetails, PageContainerParamId } from '~/types/page-container'
import type { IPageContainerFormValues, ITemplate, ITemplateConfigOptions } from '~/types/template'
import type { ITranslationRuleError } from '~/types/translation'

import DetailsHeader from './DetailsHeader'
import Editor from './Editor'
import PageSelector from './PageSelector'
import Renderer from './Renderer'
import TranslationRuleErrors from './TranslationRuleErrors'
import UploadScreenshotsModal from './UploadScreenshots'

import { createFile, uploadFile } from '~/api/file'
import { updatePageContainer } from '~/api/page-containers'
import AnimatedDots from '~/components/AnimatedDots'
import Modal from '~/components/Modal'
import ProgressBar from '~/components/ProgressBar'
import { pageContainersKeys, useCheckPageContainerTranslationsMutation } from '~/queries/page-containers'
import { PAGE_CONTAINER_STATUS } from '~/types/page-container'
import { formatPageContainerFormValues, getInitialValues } from '~/utils/page-container'
import { getBlob } from '~/utils/screenshot'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  appSummary: IAppSummary
  canEditVersion?: boolean
  isUnity: boolean
  onCompile?: () => Promise<void>
  onProcess?: () => Promise<void>
  onSubmit: (data: IPageContainerCreateUpdate) => Promise<void>
  pageContainerDetails?: IPageContainerDetails
  resetHeaderDetails?: boolean
  template: ITemplate
  templateConfig: ITemplateConfigOptions
  templateVersion: string
}

const PageContainerForm = ({
  appSummary,
  canEditVersion,
  isUnity,
  onCompile,
  onProcess,
  onSubmit,
  pageContainerDetails,
  resetHeaderDetails,
  template,
  templateConfig,
  templateVersion,
}: Props): ReactElement => {
  const { pageContainerId } = useParams<{ pageContainerId: PageContainerParamId }>()

  const queryClient = useQueryClient()

  const [pageIndex, setPageIndex] = useState(0)

  const [screenshotCounter, setScreenshotCounter] = useState(0)

  const [isLoading, setIsLoading] = useState(false)

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)

  const [translationRuleErrors, setTranslationRuleErrors] = useState<ITranslationRuleError[]>([])

  const [isUploadScreenshotsModalOpen, setIsUploadScreenshotsModalOpen] = useState(false)

  const pageContainerBuilderClasses = classNames('page-container-builder', { [templateConfig.orientation]: true })

  const screenshotTotal = templateConfig.config.length

  const { mutateAsync: checkPageContainerTranslationsMutation } = useCheckPageContainerTranslationsMutation()

  const { mutateAsync: updatePageContainerMutation } = useMutation({ mutationFn: updatePageContainer })

  const handleSelectorClick = (index: number) => {
    setPageIndex(index)
  }

  const handleErrorClicked = () => {
    setIsErrorModalOpen(false)
  }

  const toggleUploadScreenshotsModal = (isOpen: boolean) => () => setIsUploadScreenshotsModalOpen(isOpen)

  const generateScreenshots = async (): Promise<string[]> =>
    new Promise((resolve, reject) => {
      try {
        if (pageContainerDetails) {
          let page = 0
          const screenshotFileIds: string[] = new Array(screenshotTotal)

          const loop = async () => {
            setScreenshotCounter((value) => value + 1)
            const domNode = document.getElementById('renderer-node')

            if (domNode) {
              const blob = await getBlob(domNode)
              const { id, upload_url } = await createFile({
                mimeType: 'image/png',
                name: `${pageContainerDetails?.title || 'screenshot'}-${page + 1}.png`,
                size: blob.size,
              })
              await uploadFile({ file: blob, mimeType: 'image/png', uploadUrl: upload_url })
              screenshotFileIds[page] = id
            }

            page++
            if (page < screenshotTotal) {
              setPageIndex(page)
              setTimeout(loop, 500)
            } else {
              setPageIndex(0)
              setScreenshotCounter(0)
              resolve(screenshotFileIds.filter((id) => !!id))
            }
          }
          loop()
        }
      } catch (e) {
        reject(e)
      }
    })

  const handleGenerateScreenshots = async () => {
    if (!pageContainerId) return

    try {
      const screenshotFileIds = await generateScreenshots()
      setIsLoading(true)
      await updatePageContainerMutation({ id: pageContainerId, screenshotFileIds, title: pageContainerDetails?.title })
      queryClient.invalidateQueries({ queryKey: pageContainersKeys.details(pageContainerId) })
      displayToast('Screenshots successfully generated', 'success')
    } catch {
      displayToast('Screenshots could not be generated', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleUploadScreenshots = async (files: File[], remainingScreenshots?: Record<string, string>) => {
    if (!pageContainerId) return

    try {
      const fileIds = new Array(files.length)
      await Promise.all(
        files.map(async (file, index) => {
          const { id, upload_url } = await createFile({
            mimeType: file.type,
            name: file.name,
            size: file.size,
          })
          await uploadFile({ file, mimeType: file.type, uploadUrl: upload_url })
          fileIds[index] = id
        })
      )
      await updatePageContainerMutation({
        id: pageContainerId,
        screenshotFileIds: [...Object.keys(remainingScreenshots || {}), ...fileIds],
        title: pageContainerDetails?.title,
      })
      displayToast('Screenshots successfully uploaded', 'success')
      setIsUploadScreenshotsModalOpen(false)
    } catch {
      displayToast('Screenshots could not be uploaded', 'error')
    }
  }

  const handleProcess = async (values: IPageContainerFormValues) => {
    if (pageContainerId) {
      try {
        const screenshotFileIds = await generateScreenshots()
        setIsLoading(true)
        const formattedData = formatPageContainerFormValues(values)
        await onSubmit({ ...formattedData, screenshotFileIds })
        const { data: errors } = await checkPageContainerTranslationsMutation(pageContainerId)
        if (errors && errors.length) {
          displayToast('Some placeholders do not meet the rules', 'error')
          setTranslationRuleErrors(errors)
          setIsErrorModalOpen(true)
        } else {
          setTranslationRuleErrors([])
          onProcess && (await onProcess())
        }
      } catch {
        displayToast('Page container could not be compiled', 'error')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleCompile = async () => {
    if (pageContainerId) {
      try {
        setIsLoading(true)
        onCompile && (await onCompile())
      } catch {
        displayToast('Page container could not be compiled', 'error')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleSubmit = async (
    data: IPageContainerFormValues,
    { setSubmitting }: FormikHelpers<IPageContainerFormValues>
  ) => {
    const formattedData = formatPageContainerFormValues(data)
    await onSubmit(formattedData)
    setSubmitting(false)
  }

  const initialValues = getInitialValues({
    isUnity,
    pageContainerDetails,
    pageContainerTemplate: template,
    selectedApp: appSummary,
    templateConfig,
    templateVersion,
  })

  const existingScreenshots = pageContainerDetails?.screenshot_file_urls?.reduce(
    (prev, url, index) => {
      if (pageContainerDetails?.screenshot_file_ids?.[index]) {
        prev[pageContainerDetails.screenshot_file_ids[index]] = url
      } else {
        prev[index.toString()] = url
      }
      return prev
    },
    {} as Record<string, string>
  )

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
      {({ handleChange, isSubmitting, setFieldValue, values }) => {
        return (
          <Form>
            {screenshotCounter > 0 ? (
              <Portal>
                <div className="screenshot-loading-overlay">
                  <div className="screenshot-loading-overlay__title">
                    Generating screenshots
                    <AnimatedDots />
                  </div>
                  <div className="screenshot-loading-overlay__counter">
                    {screenshotCounter} / {screenshotTotal}
                  </div>
                  <div className="screenshot-loading-overlay__progress">
                    <ProgressBar percentage={(screenshotCounter * 100) / screenshotTotal} />
                  </div>
                </div>
              </Portal>
            ) : null}
            <DetailsHeader
              onTemplateVersionChange={
                canEditVersion
                  ? (version) => {
                      setFieldValue('templateVersion', version)
                    }
                  : undefined
              }
              onTitleChange={handleChange}
              pageContainer={resetHeaderDetails ? undefined : pageContainerDetails}
              status={pageContainerDetails?.status || PAGE_CONTAINER_STATUS.DRAFT}
              title={values.title}
              values={values}
            />
            <PageSelector
              isLoading={isLoading || isSubmitting}
              onCompile={handleCompile}
              onGenerateScreenshots={handleGenerateScreenshots}
              onProcess={() => handleProcess(values)}
              onSelectorClick={handleSelectorClick}
              onTranslationErrorsClick={translationRuleErrors.length ? () => setIsErrorModalOpen(true) : undefined}
              onUploadScreenshots={toggleUploadScreenshotsModal(true)}
              pageContainer={pageContainerDetails}
              pageIndex={pageIndex}
              template={templateConfig}
            />
            <div className={pageContainerBuilderClasses}>
              <div className={classNames('editor', { 'full-width': templateConfig.config[pageIndex].hideRenderer })}>
                <Editor
                  onFieldClick={(placeholderKey: string) => setFieldValue('focus', placeholderKey)}
                  pageIndex={pageIndex}
                  template={templateConfig}
                />
              </div>
              {!templateConfig.config[pageIndex].hideRenderer && (
                <div className="renderer">
                  <Renderer onSelectorClick={handleSelectorClick} pageIndex={pageIndex} template={templateConfig} />
                </div>
              )}
            </div>
            <Modal
              className="translation-rule-errors-modal"
              isOpen={isErrorModalOpen}
              onCloseModal={() => setIsErrorModalOpen(false)}
            >
              <TranslationRuleErrors errors={translationRuleErrors} onRowClicked={handleErrorClicked} />
            </Modal>
            <Modal
              className="upload-screenshots-modal"
              isOpen={isUploadScreenshotsModalOpen}
              onCloseModal={toggleUploadScreenshotsModal(false)}
            >
              <UploadScreenshotsModal
                existingScreenshots={existingScreenshots}
                onClose={toggleUploadScreenshotsModal(false)}
                onSubmit={handleUploadScreenshots}
              />
            </Modal>
          </Form>
        )
      }}
    </Formik>
  )
}

export default PageContainerForm
